/**
 * One live-graphs provider for a whole study (ENG-2450).
 *
 * The Overview's model, the expected-vs-solved table, the sweep strip, the
 * delta table, the appendix, the report's comparison table and its context
 * section all want the same thing: every design point's saved hand
 * calculation, without asking the backend for any of them. They used to ask
 * for it each: seven components, each mounting its own `useQueries` over
 * every run, so a study of twenty-four points carried a hundred and sixty
 * eight observers on one page and every cache write woke all of them.
 *
 * The study layout mounts this once. Everything below reads the context, so
 * there is exactly one observer per design point no matter how many surfaces
 * are on screen — which is what `handCalcGraphs.test.tsx` pins.
 *
 * Nothing here fetches in the study layout. `queryFn: skipToken` means these
 * observers read whatever `useHandCalcRecord` has already put in the cache
 * and never start a request of their own: opening a report must not draft a
 * record.
 *
 * The public share page is the one exception (`readSaved`, ENG-2861). Nobody
 * there opens a panel, so a cache-only table would say "not generated" beside
 * a record the PDF prints. It reads each completed point's saved record
 * through `GET …/hand-calc/record`, which never drafts, into that read's own
 * entry — not the panel's, so the export snapshot (which carries only
 * `'hand-calc'` entries) sends an anonymous reader no live preview and the
 * PDF keeps the stored report's rows.
 */
import {
  skipToken,
  useQueries,
  type UseQueryResult,
} from '@tanstack/react-query';
import { createContext, useCallback, useContext, type ReactNode } from 'react';
import type {
  DesignPointResponse,
  HandCalcGraphResponse,
} from '@/api/generated/types.gen';
import {
  handCalcRecordKey,
  savedHandCalcRecordOptions,
} from './useHandCalcRecord';

export interface LiveGraph<T = DesignPointResponse> {
  dp: T;
  data?: HandCalcGraphResponse;
}

const HandCalcGraphsContext = createContext<LiveGraph[] | null>(null);

/**
 * Every design point's record, as this browser already holds it.
 *
 * A run whose response names another attempt is reported with no data rather
 * than with the old one: the point was re-run, and the numbers beside it are
 * not about the run on screen.
 */
function useHandCalcGraphsQueries(
  study: string,
  points: DesignPointResponse[],
  readSaved: boolean,
): LiveGraph[] {
  const combine = useCallback(
    (results: UseQueryResult[]) =>
      points.map((dp, index) => {
        const data = results[index]?.data as HandCalcGraphResponse | undefined;
        return data && data.graph.attempt_id === dp.simulation_id
          ? { dp, data }
          : { dp };
      }),
    [points],
  );
  return useQueries({
    combine,
    queries: points.map((dp) => ({
      ...(readSaved
        ? {
            ...savedHandCalcRecordOptions(study, dp.id),
            enabled: dp.status === 'completed' && Boolean(dp.simulation_id),
          }
        : {
            queryKey: handCalcRecordKey(study, dp.id, dp.simulation_id ?? ''),
            queryFn: skipToken,
          }),
      staleTime: 'static' as const,
    })),
  });
}

export function HandCalcGraphsProvider({
  studyId,
  designPoints,
  readSaved = false,
  children,
}: {
  studyId: string;
  designPoints: DesignPointResponse[];
  /** Fetch each saved record read-only — the share page, and only there. */
  readSaved?: boolean;
  children: ReactNode;
}) {
  const graphs = useHandCalcGraphsQueries(studyId, designPoints, readSaved);
  return (
    <HandCalcGraphsContext.Provider value={graphs}>
      {children}
    </HandCalcGraphsContext.Provider>
  );
}

/**
 * The study's records, narrowed to the points the caller cares about.
 *
 * Narrowing here rather than mounting a second set of observers is the whole
 * point of the provider: a panel that shows one run reads the one row it
 * wants out of the list the layout is already watching.
 */
export function useHandCalcGraphs(points?: DesignPointResponse[]): LiveGraph[] {
  const all = useContext(HandCalcGraphsContext);
  // Outside a study layout — a story, a test harness — there is nothing to
  // read, and a panel that renders "no calculation yet" is the honest answer.
  if (!all) return (points ?? []).map((dp) => ({ dp }));
  if (!points) return all;
  const byId = new Map(all.map((graph) => [graph.dp.id, graph]));
  return points.map((dp) => byId.get(dp.id) ?? { dp });
}
